import { useRef, useState, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMeQuery } from '@platform/state/api';
import { Icon } from '@platform/ui';
import { routes } from '@platform/core/lib/routes';
import { useOpenInWorkspace } from '@platform/navigation';
import { appRoutes } from './appRoutes';
import type { IWorkspaceSwitcherProps } from './IWorkspaceSwitcherProps';
import { memberWorkspaces } from './memberWorkspaces';
import { useWorkspaceMeta } from './useWorkspaceMeta';
import { WorkspaceMenu } from './WorkspaceMenu';
import { WorkspaceNavigator } from './wsnav/WorkspaceNavigator';

/**
 * Перемикач робочих просторів (UIUX top-bar, прототип v4): кнопка 236×40 — логотип 28px (синьо-жовтий, біла облямівка; клік — Головна
 * простору), назва 700 з «…», «тип · N учасників» 11px (ховається вужче 1180px), шеврон; меню — простори користувача за типами.
 */
export function WorkspaceSwitcher({ ws, open, onToggle, onClose }: IWorkspaceSwitcherProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const openIn = useOpenInWorkspace();
  const button = useRef<HTMLButtonElement>(null);
  const [browse, setBrowse] = useState(false);
  const { data: me } = useMeQuery();
  const { workspace: current, meta } = useWorkspaceMeta(ws);
  const select = (id: string) => { onClose(); setBrowse(false); openIn(id, routes.home()); };
  const home = (e: MouseEvent) => { e.stopPropagation(); onClose(); navigate(routes.home()); };
  return (
    <div className="tb-ws">
      <button ref={button} type="button" className="tb-ws__btn" title={current?.name} aria-label={current?.name} aria-haspopup="menu" aria-expanded={open} onClick={onToggle}>
        <span className="tb-ws__logo" role="link" title={t('shell.workspaceHome')} aria-label={t('shell.workspaceHome')} onClick={home} />
        <span className="tb-ws__text">
          <span className="tb-ws__name">{current?.name ?? '…'}</span>
          <span className="tb-ws__meta">{meta}</span>
        </span>
        <Icon name="chevron-down" size={12} strokeWidth={2.4} className="tb-ws__chev" />
      </button>
      {open && me && <WorkspaceMenu workspaces={memberWorkspaces(me.workspaces)} current={ws} onSelect={select} onClose={onClose} onBrowse={() => setBrowse(true)} onCreate={() => navigate(appRoutes.newWorkspace())} trigger={button} />}
      {/* Діалог — у <body>: поза шапкою він не успадковує білий текст і контекст накладання верхньої панелі */}
      {browse && createPortal(<WorkspaceNavigator ws={ws} onOpen={select} onClose={() => setBrowse(false)} />, document.body)}
    </div>
  );
}
